import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import {
  rulesArePreviewable,
  toRulesInput,
  type RulesForm,
  type RuleValueKind,
} from '@/features/product-rules';

import { usePreviewRules } from '../hooks/use-preview-rules';
import type { CollectionFormInput } from '../schemas/collection-form-schema';

import { CollectionProductRow } from './collection-product-row';

export interface RulesPreviewProps {
  storeId: string;
  kindOf: (field: string) => RuleValueKind;
}

const DEBOUNCE_MS = 500;

/** Live count and first matches for the rules being edited; waits half a second after the last keystroke. */
export function RulesPreview({ storeId, kindOf }: RulesPreviewProps) {
  const { t } = useTranslation('collections');
  const { control } = useFormContext<CollectionFormInput>();
  const live = useWatch({ control, name: 'rules' });
  const liveJson = JSON.stringify(live);
  const [settledJson, setSettledJson] = useState(liveJson);

  useEffect(() => {
    if (settledJson === liveJson) return;
    const id = window.setTimeout(() => setSettledJson(liveJson), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [liveJson, settledJson]);

  const rules = useMemo(() => {
    const settled = JSON.parse(settledJson) as RulesForm;
    return rulesArePreviewable(settled) ? toRulesInput(settled, kindOf) : null;
  }, [settledJson, kindOf]);

  const preview = usePreviewRules(storeId, rules);

  return (
    <FormSection
      title={t('sections.preview')}
      description={t('sections.previewDescription')}
      tour="collections.edit.rules-preview"
    >
      {rules === null ? (
        <p className="text-muted-foreground text-sm" role="status">
          {t('preview.incomplete')}
        </p>
      ) : preview.isPending ? (
        <div className="flex flex-col gap-2" aria-busy>
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : preview.isError ? (
        <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
      ) : (
        <>
          <p className="text-sm font-medium" role="status" aria-live="polite">
            {t('preview.total', { count: preview.data.total })}
            {preview.data.total > preview.data.items.length ? (
              <span className="text-muted-foreground font-normal">
                {' '}
                {t('preview.showingFirst', { count: preview.data.items.length })}
              </span>
            ) : null}
          </p>
          {preview.data.items.length > 0 ? (
            <ol className="divide-y rounded-md border" aria-label={t('sections.preview')}>
              {preview.data.items.map((item) => (
                <CollectionProductRow key={item.productId} item={item} />
              ))}
            </ol>
          ) : null}
        </>
      )}
    </FormSection>
  );
}
